/**
 * RefundModal — ручная пометка возврата (v1, без провайдера): причина +
 * подтверждение, создаётся отдельная запись «Возврат». Десктоп — центр-
 * модалка с карточкой платежа; мобилка — bottom-sheet. Контент общий.
 */

import {useEffect, useState} from 'react';
import {Loader2, X} from 'lucide-react';

import type {PaymentRow} from '@services/payments';
import {SheetGrabber} from '@components/SheetGrabber';
import {Button} from '@components/ui/Button';
import {Dialog, DialogContent} from '@components/ui/Dialog';
import {Sheet, SheetContent} from '@components/ui/Sheet';

import {fmtRowDate, fmtSignedSom} from '../Payments.utils';

type RefundModalProps = {
  payment: PaymentRow | null;
  open: boolean;
  pending: boolean;
  mobile?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
};

export const RefundModal = ({
  payment,
  open,
  pending,
  mobile = false,
  onOpenChange,
  onConfirm,
}: RefundModalProps) => {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const body = (
    <>
      {mobile ? (
        <SheetGrabber />
      ) : (
        <div className="mb-1.5 flex items-center justify-between">
          <h3 className="text-[17px] font-bold">Отметить возврат</h3>
          <button
            onClick={() => onOpenChange(false)}
            aria-label="Закрыть"
            className="grid size-[30px] place-items-center rounded-lg bg-secondary text-muted-foreground"
          >
            <X className="size-4" strokeWidth={2} />
          </button>
        </div>
      )}
      {mobile && <h3 className="text-[17px] font-bold">Отметить возврат</h3>}

      <p className="mt-1.5 text-[13px] leading-[1.5] text-muted-foreground">
        Ручная пометка (v1, без провайдера). Создаётся отдельная запись «Возврат» со ссылкой на этот
        платёж.
      </p>

      {!mobile && payment && (
        <div className="mt-4 flex items-center justify-between rounded-[11px] border border-skr-line2 bg-skr-head px-4 py-[13px]">
          <div>
            <div className="text-[13.5px] font-semibold">{payment.client}</div>
            <div className="font-mono text-xs text-muted-foreground">
              {payment.type} · {payment.period} · {fmtRowDate(payment.date)}
            </div>
          </div>
          <div className="font-mono font-semibold">{fmtSignedSom(payment.amount)}</div>
        </div>
      )}

      <label className="mt-4 block text-[12.5px] font-semibold text-skr-ink2">
        Причина возврата
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Например: двойное списание, клиент отменил в день оплаты…"
        className="mt-2 min-h-[90px] w-full resize-none rounded-xl border border-input bg-card px-3.5 py-3 text-sm leading-[1.5] outline-none focus:border-primary"
      />

      <div className="mt-4 flex gap-2.5">
        <Button
          variant="outline"
          className="h-12 flex-1 rounded-[12px]"
          disabled={pending}
          onClick={() => onOpenChange(false)}
        >
          Отмена
        </Button>
        <Button
          className="h-12 flex-1 rounded-[12px] bg-skr-warn text-white hover:bg-skr-warn/90"
          disabled={pending}
          onClick={() => onConfirm(reason)}
        >
          {pending ? <Loader2 className="size-4 animate-spin" /> : 'Отметить возврат'}
        </Button>
      </div>
    </>
  );

  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" showClose={false} className="gap-0 rounded-t-[22px] p-5 pb-6">
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        className="w-[440px] max-w-[calc(100vw-2rem)] gap-0 rounded-[18px] p-6"
      >
        {body}
      </DialogContent>
    </Dialog>
  );
};
